import { integrationDesignMode } from "./design-mode";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  EsmProjectEditor,
  authoringStyles,
  type EsmProjectEditorProps,
} from "@proof/authoring";
import { EsmProjectSession } from "@proof/legacy";
import type { Project } from "@proof/core";

const Base =
  typeof HTMLElement === "undefined"
    ? (class {} as typeof HTMLElement)
    : HTMLElement;
export class ProofEsmProjectElement extends Base {
  private root?: Root;
  private input?: unknown;
  private current?: Project;
  private settings: Pick<EsmProjectEditorProps, "projectId" | "services"> = {};
  private save?: EsmProjectEditorProps["onSave"];
  private epoch = 0;
  connectedCallback() {
    if (!this.shadowRoot) {
      const shadow = this.attachShadow({ mode: "open" }),
        style = document.createElement("style"),
        mount = document.createElement("div");
      style.textContent = authoringStyles;
      mount.dataset.esmProjectMount = "";
      mount.style.height = "100%";
      shadow.append(style, mount);
    }
    this.root ||= createRoot(
      this.shadowRoot!.querySelector<HTMLDivElement>(
        "[data-esm-project-mount]",
      )!,
    );
    this.render();
  }
  disconnectedCallback() {
    this.epoch++;
    this.root?.unmount();
    this.root = undefined;
  }
  set value(value: unknown) {
    const session = new EsmProjectSession(value, this.settings);
    this.input = structuredClone(value);
    this.current = session.project;
    this.epoch++;
    this.render();
  }
  get value(): Project | undefined {
    return structuredClone(this.current);
  }
  set options(value: typeof this.settings) {
    const next = { ...value };
    const session =
      this.input === undefined
        ? undefined
        : new EsmProjectSession(this.input, next);
    this.settings = next;
    this.current = session?.project;
    this.epoch++;
    this.render();
  }
  get options() {
    return { ...this.settings };
  }
  set onSave(value: EsmProjectEditorProps["onSave"]) {
    this.save = value;
    this.render();
  }
  get onSave() {
    return this.save;
  }
  private event(name: string, detail: unknown) {
    this.dispatchEvent(
      new CustomEvent(`proof:esm-project-${name}`, {
        detail: structuredClone(detail),
        bubbles: true,
        composed: true,
      }),
    );
  }
  private render() {
    integrationDesignMode(this);
    if (!this.root) return;
    this.root.render(
      React.createElement(EsmProjectEditor, {
        ...this.settings,
        value: this.input,
        onChange: (project, esm) => {
          this.current = structuredClone(project);
          this.input = structuredClone(esm);
          this.epoch++;
          this.event("change", { project, esm });
        },
        onSave: async (output) => {
          if (!this.save)
            throw new Error(
              "Configure onSave to persist changes through your host application.",
            );
          const epoch = this.epoch;
          await this.save(structuredClone(output));
          if (!this.isConnected || epoch !== this.epoch)
            throw new Error(
              "Project changed while saving. Review the current source.",
            );
          this.current = structuredClone(output.project);
          this.event("save", output);
        },
      }),
    );
  }
}
export function registerProofEsmProject() {
  if (
    typeof customElements !== "undefined" &&
    !customElements.get("proof-esm-project")
  )
    customElements.define("proof-esm-project", ProofEsmProjectElement);
}
